import fetchData from './fetchData';
import { TERMS } from './kpiDefinitions';

export interface WebinarRegistrationTimeData {
  email: string;
  first_signup: string;
  first_registration: string;
  days_to_register: number;
  product_code: string;
}

export interface WebinarRegistrationRateData {
  total_emails: number; // Total signups in timeframe
  emails_with_registrations: number; // Unique emails with any registrations (all-time)
  total_registrations: number; // Total registration count (all-time)
  registered_emails: number; // Emails that registered in timeframe
  registration_rate: number; // Primary registration rate
  total_registration_rate: number; // All-time registration rate
}

export interface WebinarRegistrationDistributionData {
  time_bucket: string;
  count: number;
  percentage: number;
}

export interface ProductRegistrationData {
  product_code: string;
  product_name: string;
  registered_emails: number;
  total_registrations: number;
  avg_days_to_register: number;
  registration_rate: number;
  total_emails: number;
}

export interface WebinarRegistrationTimelineData {
  month: string;
  new_signups: number;
  registrations: number;
  monthly_registration_rate: number;
}

export interface WebinarPerformanceData {
  webinar_id: string;
  webinar_name: string;
  product_code: string;
  product_name: string;
  registrations: number;
  avg_days_to_register: number;
  first_registration: string;
  last_registration: string;
}

export interface WebinarRegistrationDetailData {
  email: string;
  signup_date: string;
  first_registration_date: string | null;
  days_to_register: number;
  product_code: string;
  product_name: string;
  webinar_id: string;
  webinar_name: string;
  affiliate?: string;
  adref?: string;
  client?: string;
}

/**
 * Kennzahlen einer Kategorie (Webinar, Kanal, Geraet, Summe). Spiegel von
 * node-app/src/helpers/webinarEvaluation.ts `EvaluationMetrics` — ALLE Quoten rechnet das Backend
 * (`computeMetrics`), das Frontend rechnet keine nach. Bedeutung jedes Felds:
 * helpers/webinarKpiDefinitions.ts.
 */
export interface EvaluationMetrics {
  category: string;
  /** Personen mit Anmeldung; in der Sektions-Sicht (/auswertungen) nur mit Live-Anmeldung (rechtzeitig/Späteinlass). */
  registrations: number;
  /** Sektions-Sicht: Personen nur mit Anmeldung nach Sendestart (nicht in `registrations`); sonst 0. */
  registrations_after_start?: number;
  timely: number;
  attendees: number;
  orders: number;
  orders_with_registration: number;
  /** Mit Anmeldung, ohne vorläufig ergänzte (Baustein der Anfo-Überleitung, 27.10.2026). */
  orders_with_registration_fest?: number;
  attendee_orders: number;
  cpa_revenue: number;
  orders_without_cpa: number;
  ltv_revenue: number;
  orders_without_ltv: number;
  paid_revenue: number;
  orders_without_article: number;
  upsell_orders: number;
  active_subscribers: number;
  /** Sektions-Sicht: vorläufig aus PX-Data ergänzte Anfos (Zahlstand unbekannt); sonst 0. */
  orders_provisional?: number;
  /** Nicht summierbar; nach dem Zusammenlegen von Kategorien null. */
  unique_customers: number | null;
  show_up_rate: number | null;
  registration_conversion_rate: number | null;
  attendee_conversion_rate: number | null;
  cpa_per_registration: number | null;
  cpa_per_attendee: number | null;
  ltv_per_registration: number | null;
  ltv_per_attendee: number | null;
  paid_per_registration: number | null;
  avg_cpa_per_order: number | null;
  avg_ltv_per_order: number | null;
  avg_paid_per_order: number | null;
  upsell_rate: number | null;
  active_subscriber_rate: number | null;
}

export interface WebinarRevenuePerformanceData extends EvaluationMetrics {
  webinar_id: string;
  webinar_title: string;
  type: string;
  is_evergreen: number;
  webinar_datetime: string;
  product_code: string | null;
  product_name: string | null;
  /** StealthSeminar hat kaum Teilnahme-Signale geliefert — Show-up/Teiln.-Conv. nicht belastbar. */
  tracking_suspect: boolean;
}

export interface WebinarRevenueSummaryData extends EvaluationMetrics {
  distinct_persons: number;
  webinar_count: number;
  webinars_with_orders: number;
  /** webinar_ids mit Tracking-Warnung — ihre Teilnahme zaehlt in der Summenzeile nicht mit. */
  webinars_excluded_for_tracking: string[];
}

export interface WebinarRevenueProductData extends EvaluationMetrics {
  product_code: string;
  product_name: string;
}

export interface WebinarRevenueTypeData extends EvaluationMetrics {
  type: string;
  webinar_count: number;
}

export interface WebinarRevenueTimelineData extends EvaluationMetrics {
  month: string;
}

export interface WebinarRevenueAttributionData {
  performance: WebinarRevenuePerformanceData[];
  summary: WebinarRevenueSummaryData;
  products: WebinarRevenueProductData[];
  types: WebinarRevenueTypeData[];
  timeline: WebinarRevenueTimelineData[];
}

export interface WebinarOrderMappingData {
  ordernumber: string;
  mandatorid: string;
  email: string;
  webinar_id: string | null;
  webinar_title: string | null;
  mapping_method:
    | 'rid'
    | 'banner_webinar_id'
    | 'email_banner'
    | 'email_doi_url'
    | 'email_affiliate'
    | 'affiliate_concept'
    | 'unmapped';
  created_on: string;
  rid: string | null;
  banner_date: string | null;
  registered_email: string | null;
  mapping_updated_at: string;
  offerurl: string | null;
  banner: string | null;
  webinar_start_date: string | null;
  affiliate: string | null;
  adref: string | null;
  registration_email: string | null;
}

export const fetchWebinarRegistrationTime = async (
  startDate?: string,
  endDate?: string,
  clients?: string[],
): Promise<WebinarRegistrationTimeData[]> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  return fetchData<WebinarRegistrationTimeData>(
    '/api/webinar-statistics/registration-time',
    params,
    true,
  ) as Promise<WebinarRegistrationTimeData[]>;
};

export const fetchWebinarRegistrationRate = async (
  startDate?: string,
  endDate?: string,
  clients?: string[],
): Promise<WebinarRegistrationRateData> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  return fetchData<WebinarRegistrationRateData>(
    '/api/webinar-statistics/registration-rate',
    params,
    false,
  ) as Promise<WebinarRegistrationRateData>;
};

export const fetchWebinarRegistrationDistribution = async (
  startDate?: string,
  endDate?: string,
  clients?: string[],
): Promise<WebinarRegistrationDistributionData[]> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  return fetchData<WebinarRegistrationDistributionData>(
    '/api/webinar-statistics/registration-distribution',
    params,
    true,
  ) as Promise<WebinarRegistrationDistributionData[]>;
};

export const fetchProductRegistrations = async (
  startDate?: string,
  endDate?: string,
  clients?: string[],
): Promise<ProductRegistrationData[]> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  return fetchData<ProductRegistrationData>(
    '/api/webinar-statistics/product-registrations',
    params,
    true,
  ) as Promise<ProductRegistrationData[]>;
};

export const fetchWebinarRegistrationTimeline = async (
  startDate?: string,
  endDate?: string,
  clients?: string[],
): Promise<WebinarRegistrationTimelineData[]> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  return fetchData<WebinarRegistrationTimelineData>(
    '/api/webinar-statistics/registration-timeline',
    params,
    true,
  ) as Promise<WebinarRegistrationTimelineData[]>;
};

export const fetchWebinarPerformance = async (
  startDate?: string,
  endDate?: string,
  clients?: string[],
): Promise<WebinarPerformanceData[]> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  return fetchData<WebinarPerformanceData>(
    '/api/webinar-statistics/webinar-performance',
    params,
    true,
  ) as Promise<WebinarPerformanceData[]>;
};

export const fetchWebinarRegistrationDetails = async (
  timeBucket: string,
  startDate?: string,
  endDate?: string,
  clients?: string[],
): Promise<WebinarRegistrationDetailData[]> => {
  const params: any = { timeBucket };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  return fetchData<WebinarRegistrationDetailData>(
    '/api/webinar-statistics/registration-details',
    params,
    true,
  ) as Promise<WebinarRegistrationDetailData[]>;
};

export const fetchProductRegistrationDetails = async (
  productCode: string,
  startDate?: string,
  endDate?: string,
  clients?: string[],
): Promise<WebinarRegistrationDetailData[]> => {
  const params: any = { productCode };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  return fetchData<WebinarRegistrationDetailData>(
    '/api/webinar-statistics/product-registration-details',
    params,
    true,
  ) as Promise<WebinarRegistrationDetailData[]>;
};

export const fetchWebinarRevenueAttribution = async (
  startDate?: string,
  endDate?: string,
  products?: string[],
  affiliateContains?: string,
  affiliateNotContains?: string,
  adrefContains?: string,
  adrefNotContains?: string,
  adTypes?: string[],
  blogs?: string[],
): Promise<WebinarRevenueAttributionData> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (products && products.length > 0) params.products = products.join(',');
  if (affiliateContains) params.affiliateContains = affiliateContains;
  if (affiliateNotContains) params.affiliateNotContains = affiliateNotContains;
  if (adrefContains) params.adrefContains = adrefContains;
  if (adrefNotContains) params.adrefNotContains = adrefNotContains;
  if (adTypes && adTypes.length > 0) params.adTypes = adTypes.join(',');
  if (blogs && blogs.length > 0) params.blogs = blogs.join(',');
  return fetchData<WebinarRevenueAttributionData>(
    '/api/webinar-statistics/revenue-attribution',
    params,
    false,
  ) as Promise<WebinarRevenueAttributionData>;
};

/** Eine Zeile je Anfo. Kanal-Felder (affiliate, adref, registration_date) = erste Anmeldung der Person. */
export interface WebinarOrderDetailData {
  ordernumber: string;
  email: string | null;
  created_on: string;
  vertrag_sts: string | null;
  werbeaktion_wbkz: string | null;
  banner: string | null;
  product_code: string | null;
  product_name: string | null;
  cpa: number | null;
  ltv: number | null;
  paid: number;
  ohne_cpa: number;
  ohne_artikel: number;
  /** Vorläufig aus PX-Data ergänzt (nur Sektion, sonst 0): kein Euro-Wert in der Zeile. */
  is_provisional?: number;
  mapping_method: string;
  rid: string | null;
  registered_email: string | null;
  banner_date: string | null;
  has_registration: number;
  no_reg_label: string | null;
  attended: number | null;
  affiliate: string | null;
  adref: string | null;
  registration_date: string | null;
  /** Kundenstatus vor dieser Anfo (Regeln aus /auswertungen: ohne Stornos, mind. 2 h vorher, Zahlung nach heutigem Stand). */
  customer_status?: CustomerStatus;
}

export type CustomerStatus = 'neu' | 'bestellt_unbezahlt' | 'zahlend' | 'ohne_adresse';

/** Wortlaut der Kundentypen der Sektion (`TERMS.erstbesteller` usw.), auf alter und neuer Detailseite gleich. */
export const CUSTOMER_STATUS_LABELS: Record<CustomerStatus, string> = {
  neu: TERMS.erstbesteller.label,
  bestellt_unbezahlt: TERMS.frueherNieGezahlt.label,
  zahlend: TERMS.frueherGezahlt.label,
  ohne_adresse: 'Ohne Adresse',
};

/** Käufer (Personen) nach Kundenstatus vor ihrer ersten Anfo des Webinars. */
export interface BuyerStatusSummary {
  buyers: number;
  neu: number;
  bestellt_unbezahlt: number;
  zahlend: number;
  ohne_adresse: number;
}

export const fetchWebinarOrderDetails = async (
  webinarId: string,
): Promise<WebinarOrderDetailData[]> => {
  // Bewusst ohne Zeitraum: der Tab zeigt alle Anfos des Webinars, wie die Kachel darueber.
  return fetchData<WebinarOrderDetailData>(
    '/api/webinar-statistics/webinar-order-details',
    { webinarId },
    true,
  ) as Promise<WebinarOrderDetailData[]>;
};

export interface WebinarEngagementDetailData extends EvaluationMetrics {
  webinar_id: string;
  title: string;
  type: string;
  is_evergreen: number;
  start_date: string;
  tracking_suspect: boolean;
  cta_views: number;
  cta_clicks: number;
  cta_ctr: number | null;
  /** Personen mit Anfo, die im Webinar auf das Angebot geklickt haben (ohne zeitliche Reihenfolge). */
  cta_buyers?: number;
  /** Käufer unter den Klickern ÷ Klicker × 100. */
  cta_buy_rate?: number | null;
  buyer_status?: BuyerStatusSummary;
  /** Je Teilnahmegeraet (erstes Anwesenheits-Signal der Person). */
  attendance_devices: EvaluationMetrics[];
  /** Je Anmeldegeraet (erste Anmeldung der Person) — der Geraete-Funnel. */
  signup_devices: EvaluationMetrics[];
}

/** Geschätzte Zuschauer je Videominute der Erstausstrahlung im eigenen Raum; `room: null` = kein Raum. */
export interface RoomRetentionData {
  room: {
    offerMinute: number | null;
    videoMinutes: number | null;
    viewers: number;
    offerShown: number;
    offerClick: number;
    avgWatchedSec: number;
    retention: Array<{ minute: number; all: number; punctual: number }>;
  } | null;
}

/** Detailseite: dieses Webinar (erste Zeile) + die letzten Vergleichswebinare, wie die Bilder der Montagsmail. */
export interface WebinarComparisonRow {
  webinarId: string;
  title: string;
  /** Berliner Wallclock 'YYYY-MM-DD HH:mm:ss' */
  start: string;
  product: string | null;
  registrations: number;
  timely: number;
  attendees: number;
  orders: number;
  ordersWithoutCpa: number;
  cpaRevenue: number;
  ltvRevenue: number;
}

export interface WebinarComparisonData {
  rows: WebinarComparisonRow[];
  nonAttendee: { total: number; groups: Array<{ registered: boolean; detail: string; n: number }> };
  /** Fertiger Satz zu den ANFOs ohne Teilnahme (dieselbe Formulierung wie die Mail); null = keine. */
  nonAttendeeText: { head: string; text: string } | null;
}

export const fetchWebinarComparison = async (webinarId: string): Promise<WebinarComparisonData> => {
  const response = await fetchData<{ success: boolean; data: WebinarComparisonData }>(
    `/api/webinar-statistics/webinar/${encodeURIComponent(webinarId)}/comparison`,
    {},
    false,
  );
  return (response as any).data;
};

export const fetchWebinarRoomRetention = async (webinarId: string): Promise<RoomRetentionData> => {
  const response = await fetchData<{ success: boolean; data: RoomRetentionData }>(
    `/api/webinar-statistics/room-retention/${encodeURIComponent(webinarId)}`,
    {},
    false,
  );
  return (response as any).data;
};

export const fetchWebinarEngagementDetails = async (
  webinarId: string,
): Promise<WebinarEngagementDetailData> => {
  const response = await fetchData<{
    success: boolean;
    data: WebinarEngagementDetailData;
  }>(`/api/webinar-statistics/engagement-details/${webinarId}`, {}, false);
  return (response as any).data;
};

export const fetchWebinarOrderMappings = async (
  startDate?: string,
  endDate?: string,
  clients?: string[],
  mappingMethod?: string,
): Promise<WebinarOrderMappingData[]> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (mappingMethod && mappingMethod !== 'all') params.mappingMethod = mappingMethod;

  return fetchData<WebinarOrderMappingData>(
    '/api/webinar-statistics/order-mappings',
    params,
    true,
  ) as Promise<WebinarOrderMappingData[]>;
};

// Helper function to compute summary statistics from registration time data
export const computeWebinarRegistrationTimeSummary = (
  data: WebinarRegistrationTimeData[],
): {
  averageDays: number;
  medianDays: number;
  minDays: number;
  maxDays: number;
} => {
  if (!data.length) {
    return {
      averageDays: 0,
      medianDays: 0,
      minDays: 0,
      maxDays: 0,
    };
  }

  const sortedDays = data.map((item) => item.days_to_register).sort((a, b) => a - b);
  const sum = sortedDays.reduce((acc, val) => acc + val, 0);

  return {
    averageDays: Math.round((sum / sortedDays.length) * 10) / 10,
    medianDays:
      sortedDays.length % 2 === 0
        ? (sortedDays[sortedDays.length / 2 - 1] + sortedDays[sortedDays.length / 2]) / 2
        : sortedDays[Math.floor(sortedDays.length / 2)],
    minDays: sortedDays[0],
    maxDays: sortedDays[sortedDays.length - 1],
  };
};

export interface WebinarFilterOptions {
  products: { artikel: string; name: string }[];
}

export const fetchWebinarFilterOptions = async (): Promise<WebinarFilterOptions> => {
  return fetchData<WebinarFilterOptions>(
    '/api/webinar-statistics/filter-options',
    {},
    false,
  ) as Promise<WebinarFilterOptions>;
};

// Detailseite, Tab Registrierungen: jede Anmeldezeile, je Person markiert.
export interface WebinarRegistrationData {
  id: number;
  email: string;
  registration_date: string;
  affiliate: string | null;
  adref: string | null;
  rid: string | null;
  /** 0 = vor Sendestart, >0 = nach Sendestart. */
  post_start: number;
  signup_device: string;
  attended: number;
  is_first_registration: number;
  registrations_for_email: number;
  first_signup_date: string | null;
  days_to_register: number | null;
  has_order: number;
}

export const fetchWebinarRegistrations = async (
  webinarId: string,
): Promise<WebinarRegistrationData[]> => {
  return fetchData<WebinarRegistrationData>(
    `/api/webinar-statistics/webinar/${webinarId}/registrations`,
    {},
    true,
  ) as Promise<WebinarRegistrationData[]>;
};

// Mapping breakdown data
export interface MappingBreakdownItem extends EvaluationMetrics {
  mapping_method: string;
  percentage: number;
}

export interface WebinarMappingBreakdownData {
  total_orders: number;
  breakdown: MappingBreakdownItem[];
}

export const fetchWebinarMappingBreakdown = async (
  webinarId: string,
): Promise<WebinarMappingBreakdownData> => {
  return fetchData<WebinarMappingBreakdownData>(
    `/api/webinar-statistics/webinar/${webinarId}/mapping-breakdown`,
    {},
    false,
  ) as Promise<WebinarMappingBreakdownData>;
};

// Affiliate grouping overview data
export interface AffiliateGroupingCategory {
  category: string;
  patterns: string[];
  count: number;
  examples: string[];
}

export interface WebinarAffiliateGroupingOverview {
  adType: AffiliateGroupingCategory[];
  blog: AffiliateGroupingCategory[];
}

export const fetchWebinarAffiliateGroupingOverview =
  async (): Promise<WebinarAffiliateGroupingOverview> => {
    return fetchData<WebinarAffiliateGroupingOverview>(
      '/api/webinar-statistics/affiliate-grouping-overview',
      {},
      false,
    ) as Promise<WebinarAffiliateGroupingOverview>;
  };

// Grouped evaluation data
export type GroupedMetrics = EvaluationMetrics;

export interface MatrixCell extends EvaluationMetrics {
  ad_type: string;
  blog: string;
}

export interface TimelineEntry extends EvaluationMetrics {
  month: string;
}

/**
 * Gruppierung je Kanal. Jede Person steht in GENAU einer Kategorie (erste Anmeldung), die Zeilen
 * summieren also auf `totals.all`. `heatUp` ist die Heat-Up-Teilmenge.
 */
export interface WebinarGroupedEvaluationDetailData {
  totals: { all: EvaluationMetrics; heatUp: EvaluationMetrics | null };
  byAdType: EvaluationMetrics[];
  byBlog: EvaluationMetrics[];
  byHeatUpNummer: EvaluationMetrics[];
  byAffiliate: EvaluationMetrics[];
  byAttendanceDevice: EvaluationMetrics[];
  bySignupDevice: EvaluationMetrics[];
  /** Kohorten nach Anmeldeabstand zum Webinar-Start (+ Anfos ohne Anmeldung unter ihrem Etikett). */
  bySignupLead?: EvaluationMetrics[];
  matrix: MatrixCell[];
}

export interface WebinarGroupedEvaluationData extends WebinarGroupedEvaluationDetailData {
  /** webinar_ids mit Tracking-Warnung — ihre Teilnahme zaehlt in keiner Zeile dieses Tabs mit. */
  webinars_excluded_for_tracking: string[];
  timeline: {
    byAdType: TimelineEntry[];
    byBlog: TimelineEntry[];
  };
}

export const fetchWebinarGroupedEvaluation = async (
  startDate?: string,
  endDate?: string,
  products?: string[],
): Promise<WebinarGroupedEvaluationData> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (products && products.length > 0) params.products = products.join(',');
  return fetchData<WebinarGroupedEvaluationData>(
    '/api/webinar-statistics/grouped-evaluation',
    params,
    false,
  ) as Promise<WebinarGroupedEvaluationData>;
};

// Quellen-Effizienz (FT): Werbekontakte + Anfos je 1.000 Kontakte je Akquise-Quelle.
// `kontakte === null` heisst "keine belastbare Reichweite" — NIE als 0 rendern, sonst wird aus
// unbekannt eine Aussage. Gilt genauso fuer die abgeleiteten _pro_1k-Felder.
export interface SourceEfficiencyRow {
  quelle: string;
  reach_kind: 'send_metrics' | 'newsletter_edition' | 'redlink_send' | 'none';
  no_reach_reason: string | null;
  /**
   * true = der Nenner enthält externe Ausgaben, deren Anzeigen-Platzierung aus dem Webinar-Kalender
   * rekonstruiert ist (kein Platzierungs-Protokoll vorhanden). NICHT als Messwert darstellen.
   */
  reach_reconstructed: boolean;
  /** Versaende bzw. Ausgaben, die diese Zeile speisen. */
  einheiten: number;
  einheiten_ohne_reichweite: number;
  kontakte: number | null;
  anmeldungen: number;
  anfos: number;
  cpa_revenue: number;
  anmeldungen_pro_1k: number | null;
  anfos_pro_1k: number | null;
  anfo_quote: number | null;
}

export interface SourceEfficiencyData {
  rows: SourceEfficiencyRow[];
  /**
   * FT-Gesamtzahl im Fenster. Die Zeilen summieren per Konstruktion darauf (Residual-Zeile
   * "Nicht zuordenbar" fuellt die Luecke). `covered_*` ist der Teil, der einem konkreten Versand
   * zugeordnet werden konnte — nur der kann in einer Quote stehen.
   */
  totals: {
    anmeldungen: number;
    anfos: number;
    anfo_quote: number | null;
    covered_anmeldungen: number;
    covered_anfos: number;
    /** true = Zeilen liegen ueber der Gesamtzahl, die Definitionen widersprechen sich. Nie stillschweigend klemmen. */
    residual_negative: boolean;
  };
  meta: {
    /** Anzeigename der gewählten Marken, kommasepariert. */
    publisher: string;
    publishers: string[];
    /** 'raw' = jeder angeschriebene Kontakt, 'normalized' = nur der aktive Teil der Reaktivierungs-Versände. */
    reach_basis: 'raw' | 'normalized';
    /** Anzahl Reaktivierungs-Versände im Fenster — nur dann ist die Umschaltung folgenreich. */
    reaktivierungs_sends: number;
    /** Gewählte Marken ohne Newsletter-Reichweite — erklärt "—" in den Newsletter-Zeilen. */
    publishers_without_newsletter_reach: string[];
    publisher_options: { key: string; label: string; has_newsletter_reach: boolean }[];
    window: { from: string; to: string };
    /** null, wenn das ganze Fenster vor der Datengrenze liegt — dann gibt es kein Reichweitenfenster. */
    reach_window: { from: string; to: string } | null;
    reach_clamped: boolean;
    reach_available: boolean;
    reach_data_floor: string;
    overlap_note: string;
  };
}

// Anders als die Nachbar-Endpoints dieser Seite antwortet /source-efficiency in der
// Hauskonvention `{ success, data }` (CLAUDE.md § Conventions). `fetchData` reicht den Body
// unveraendert durch, packt also NICHTS aus — ohne dieses Unwrap bekaeme die Komponente den
// Umschlag statt des Inhalts und stuerbe an `meta.publisher`.
export const fetchWebinarSourceEfficiency = async (
  startDate?: string,
  endDate?: string,
  publishers?: string[],
  reachBasis?: 'raw' | 'normalized',
): Promise<SourceEfficiencyData> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  // Leere Auswahl NICHT senden — der Endpoint faellt dann auf Finanztrends zurueck, statt sie
  // als "alle" zu lesen.
  if (publishers && publishers.length > 0) params.publishers = publishers.join(',');
  if (reachBasis) params.reachBasis = reachBasis;
  const envelope = (await fetchData<any>(
    '/api/webinar-statistics/source-efficiency',
    params,
    false,
  )) as { success?: boolean; data?: SourceEfficiencyData; error?: string };
  if (!envelope?.success || !envelope.data) {
    throw new Error(envelope?.error || 'Quellen-Effizienz konnte nicht geladen werden');
  }
  return envelope.data;
};

export const fetchWebinarGroupedEvaluationDetail = async (
  webinarId: string,
): Promise<WebinarGroupedEvaluationDetailData> => {
  return fetchData<WebinarGroupedEvaluationDetailData>(
    `/api/webinar-statistics/webinar/${webinarId}/grouped-evaluation`,
    {},
    false,
  ) as Promise<WebinarGroupedEvaluationDetailData>;
};

// Spiegel der Backend-Definitionen. QUELLE DER WAHRHEIT sind
// node-app/src/helpers/webinarAdTypes.ts und node-app/src/helpers/webinarBlogs.ts — wer dort eine
// Kategorie ergaenzt, muss hier nachziehen, sonst taucht sie in der Tabelle auf und laesst sich
// nicht filtern. Genau so fehlte 'Newsletter Webinar-Ad' hier seit 2026-08-07.
export const AD_TYPE_OPTIONS = [
  'DOI-Seite',
  'Portal-Widget',
  'Direkt-Mailing',
  'KI-Ad Newsletter',
  'Heat-Up Mailing',
  'Newsletter',
  'Newsletter Webinar-Ad',
  'Redlink',
  'EPOM-AdServer',
  'Sonstige',
];

export const BLOG_OPTIONS = [
  'Finanztrends',
  'Börse-Global',
  'Börse-Express',
  'Stock-World',
  'Kapitalmarktexperten',
  'Trading-Treff',
  'DNV',
  'Listbroking',
  'Sonstige',
];

export const HEAT_UP_NUMMER_OPTIONS = ['1', '2', '3', '4+', 'Ohne Nr.'];

// ---------------------------------------------------------------------------
// Design-A/B der Anmeldeseiten (Arm A "electric" gegen Arm B "bp")
// ---------------------------------------------------------------------------

export interface DesignAbWilson {
  anteil: number | null;
  unten: number | null;
  oben: number | null;
}

export interface DesignAbArm {
  arm: 'A' | 'B';
  /** Nenner: Seitenauslieferungen aus dem nginx-Log. Aufrufe, NICHT Personen. */
  auslieferungen: number;
  /** Zaehler: Personen, dedupliziert auf die Adresse, Arm aus der ersten Anmeldung. */
  anmelder: number;
  teilgenommen: number;
  /** `null`, solange der Nenner Luecken hat — eine Quote waere dann irrefuehrend, nicht ungenau. */
  anmelder_je_1000: number | null;
  showup: DesignAbWilson;
}

export interface DesignAbRow {
  webinar_id: string;
  title: string | null;
  start_date: string;
  arme: DesignAbArm[];
  balance: { anteilA: number | null; abweichungSd: number | null; auffaellig: boolean };
  showup_vergleich: {
    differenz: number | null;
    zWert: number | null;
    pWert: number | null;
    signifikant: boolean;
    urteil: string;
  };
  nenner_vollstaendig: boolean;
  /** Anmeldungen an Tagen ohne jede Auslieferungszeile — der Beleg fuer die Luecke. */
  unabgedeckte_anmeldungen: number;
  tage_ohne_auslieferungsdaten: number;
  /** Adressen, die in BEIDEN Armen auftauchen; aus beiden herausgenommen. */
  kreuz_adressen: number;
}

export interface DesignAbData {
  rows: DesignAbRow[];
  meta: {
    from: string;
    to: string;
    /** Fruehester Tag mit Auslieferungsdaten — davor kann kein Nenner vollstaendig sein. */
    log_beginnt: string | null;
    tz_tabellen_geladen: boolean;
    zaehler_cron: {
      zuletzt: string | null;
      status: string;
      fehler_in_folge: number;
      fehler: string | null;
      gesund: boolean;
    } | null;
    hinweis: string;
  };
}

export const fetchWebinarDesignAb = async (
  startDate?: string,
  endDate?: string,
): Promise<DesignAbData> => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const envelope = (await fetchData<any>('/api/webinar-statistics/design-ab', params, false)) as {
    success?: boolean;
    data?: DesignAbData;
    error?: string;
  };
  if (!envelope?.success || !envelope.data) {
    throw new Error(envelope?.error || 'Design-A/B-Auswertung konnte nicht geladen werden');
  }
  return envelope.data;
};
